import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  formatReleaseDate,
  markReleaseAsSeen,
  isReleaseSeenByUser,
} from "@rista/core/whatsNewSection";

import { getReleaseNotesData } from "@rista/core/redux-store/slices/GlobalSlice";
import { isSupportLogin } from "@rista/core/helpers/commonHelpers/shared";

interface UseWhatsNewLogicOptions {
  onClose?: () => void;
  autoOpen?: boolean;
}

export function useWhatsNewLogic({ onClose, autoOpen = true }: UseWhatsNewLogicOptions = {}) {
  const assignedLicence = useSelector(
    (state: any) => state?.businessStore?.userData?.assignedLicence,
  );

  const dispatch = useDispatch();

  const saleItems = useSelector((state: any) => state?.saleDataStore?.saleData?.saleItems || []);

  const checkoutModalState = useSelector(
    (state: any) => state?.checkoutModalStore?.checkoutModalState,
  );
  const isCheckoutActive = useSelector((state: any) => state?.checkoutModalStore?.isCheckoutActive);

  const isCheckoutOpen =
    !!isCheckoutActive ||
    (!!checkoutModalState &&
      checkoutModalState !== "closed" &&
      checkoutModalState !== false &&
      checkoutModalState !== "");

  const hasSaleItems = Array.isArray(saleItems) && saleItems.length > 0;

  const isMidTransaction = hasSaleItems || isCheckoutOpen;

  const [activeVersionIndex, setActiveVersionIndex] = useState(0);

  const userData = useSelector((state: any) => state?.businessStore?.userData || {});
  const isOffline = useSelector((state: any) => state?.businessStore?.isOffline);

  const releaseNotes = useSelector((state: any) => state?.globalStore?.releaseNotes || null);
  const releaseNotesFetchStatus = useSelector(
    (state: any) => state?.globalStore?.releaseNotesFetchStatus ?? "idle",
  );

  const [showLaunchWhatsNewModal, setShowLaunchWhatsNewModal] = useState(false);

  const latestRelease = releaseNotes?.releases?.[0];

  const releaseHashKey = releaseNotes?.hashKey || latestRelease?.version || "";

  /**
   * Automatically open What's New when there
   * is a new release that the user has not seen.
   */
  useEffect(() => {
    if (
      !autoOpen ||
      !assignedLicence ||
      isOffline ||
      isSupportLogin() ||
      isMidTransaction ||
      showLaunchWhatsNewModal
    ) {
      return;
    }

    /**
     * Release notes have not been loaded yet.
     * Fetch them first.
     */
    if (!releaseNotes?.releases?.length && releaseNotesFetchStatus === "idle") {
      dispatch(getReleaseNotesData({}));
      return;
    }

    if (!releaseNotes?.releases?.length) {
      return;
    }

    const hasSeen = isReleaseSeenByUser(releaseHashKey, userData.businessUserId || userData.userId);

    if (hasSeen) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const latestSeen = isReleaseSeenByUser(
        releaseHashKey,
        userData.businessUserId || userData.userId,
      );

      if (!latestSeen && !isSupportLogin() && !isMidTransaction) {
        setShowLaunchWhatsNewModal(true);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    assignedLicence,
    autoOpen,
    isOffline,
    isMidTransaction,
    userData?.businessUserId,
    userData?.userId,
    releaseNotes,
    releaseNotesFetchStatus,
    releaseHashKey,
    showLaunchWhatsNewModal,
    dispatch,
  ]);

  /**
   * Releases already come from Redux.
   *
   * The releaseNotes saga is responsible for normalizing
   * the release data, so there is no need to call
   * normalizeReleaseData again here.
   */
  const releases = releaseNotes?.releases || [];

  /**
   * Keep the selected version index within
   * the available releases.
   */
  const normalizedIndex =
    releases.length > 0 ? Math.max(0, Math.min(activeVersionIndex, releases.length - 1)) : 0;

  const selectedRelease = releases[normalizedIndex] || releases[0] || latestRelease;

  const totalUpdatesCount = selectedRelease?.totalUpdatesCount || 0;

  /**
   * Change selected release version.
   */
  const handleVersionChange = (index: number) => {
    setActiveVersionIndex(index);
  };

  /**
   * Dismiss What's New.
   *
   * Mark the dismissed release as seen.
   */
  const handleOnDismiss = () => {
    const businessUserId = userData?.businessUserId || userData?.userId;
    if (businessUserId && releaseHashKey) {
      markReleaseAsSeen(releaseHashKey, businessUserId);
    }

    setShowLaunchWhatsNewModal(false);

    onClose?.();
  };

  return {
    releases,
    selectedRelease,
    normalizedIndex,
    totalUpdatesCount,
    handleVersionChange,
    handleOnDismiss,
    showLaunchWhatsNewModal,
    setShowLaunchWhatsNewModal,
    releaseHashKey,
    formatReleaseDate,
  };
}

export default useWhatsNewLogic;
